<template>
  <div>
    <Sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :is-add.sync="isAdd"
      :item-edit="itemEdit"
      @refetch-data="refetchData"
      :type-alarms-options="typeAlarmsOptions"
      :tenant-options="tenantOptions"
    />
    <b-row class="pt-2 pb-1">
      <b-col cols="12" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
    </b-row>

    <toastification-loading-content ref="loadingToast" />

    <!-- Table Container Card -->
    <b-card-group deck class="mx-2 mt-1">
      <b-card class="m-0" no-body>
        <div class="m-2">
          <!-- Table Top -->
          <b-row>
            <!-- Per Page -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label class="text-capitalize">{{ $t("table.show") }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
                data-cy="per-page-selector"
              />
              <label class="text-capitalize">{{ $t("table.entries") }}</label>
            </b-col>

            <!-- Search -->
            <b-col cols="12" md="6">
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  :placeholder="$t('table.search')"
                  data-cy="search-input"
                />
                <b-button
                  variant="primary"
                  @click="openAdd()"
                  v-if="$can('create', 'OmnicloudFrList')"
                >
                  <span class="text-nowrap text-capitalize">
                    {{ `${$t("actions.add")} ${$t("omnicloud_fr.watch_list.title")}` }}
                  </span>
                </b-button>
                <b-button
                  @click="exportExcel()"
                  variant="success"
                  class="text-capitalize ml-1"
                  type="button"
                  :disabled="buttonExport"
                  v-b-tooltip.hover
                  :title="`${$t('messages.export.title')}`"
                >
                  <feather-icon icon="DownloadCloudIcon" size="15" />
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>

        <b-table
          ref="refListTable"
          class="position-relative"
          :items="fetchList"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          :empty-text="$t('no_record_found')"
          :sort-desc.sync="isSortDirDesc"
        >
          <template #head()="data">
            <span> {{ $tc(data.label, 2) }}</span>
          </template>

          <template #head(actions)="data">
            <span> {{ $tc(data.label, 2) }}</span>
          </template>
          <template #head(color)="data">
            <span>{{ $tc(data.label, 2) }}</span>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item
                @click="openEdit(data.item)"
                v-if="$can('update', 'OmnicloudFrList')"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">{{
                  $t("actions.edit")
                }}</span>
              </b-dropdown-item>

              <b-dropdown-item
                @click="confirmDelete(data.item.id)"
                v-if="$can('delete', 'OmnicloudFrList')"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">{{
                  $t("actions.delete")
                }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>

          <template #cell(color)="data">
            <div
              v-if="data.item.color"
              style="
                width: 100%;
                display: flex;
                justify-content: start;
                align-items: center;
              "
            >
              <div
                :style="{
                  'background-color': '#' + data.item.color,
                  'border-radius': '50px',
                  height: '20px',
                  width: '20px',
                }"
              ></div>
            </div>
            <span v-else>-</span>
          </template>

          <template #cell(is_blacklist)="data">
            <span class="text-capitalize">
              {{ data.item.is_blacklist ? $t("yes") : $t("no") }}
            </span>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted text-capitalize">{{
                $t("table.showing", {
                  from: dataMeta.from,
                  to: dataMeta.to,
                  of: dataMeta.of,
                })
              }}</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <Pagination
                :totalList="totalList"
                :currentPage="currentPage"
                @updatePagination="update"
                :perPage="perPage"
                data-cy="pagination"
              >
              </Pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </b-card-group>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import useList from "./useList";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import Pagination from "@/components/Pagination.vue";
import Sidenav from "./Sidenav.vue";

export default {
  components: {
    Sidenav,
    BreadcrumbMenu,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    ToastificationLoadingContent,
    Pagination,
  },

  setup(_, context) {
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const buttonExport = ref(false);
    const loadingToast = ref(null);
    const action = ref("list");
    const exportExcel = () => {
      action.value == "export";
      buttonExport.value = true;
      loadingToast.value.show(
        context.parent.$i18n.t("messages.export.download"),
        context.parent.$i18n.t("messages.export.Generating_file"),
        "secondary"
      );
      store
        .dispatch("omnicloud_watch_list/export", {
          q: searchQuery.value,
          max: perPage.value,
          "page[n]": currentPage.value,
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
          action: "export",
        })
        .then((response) => {
          console.log(response);
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: context.parent.$i18n.t("messages.error.server_error"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          loadingToast.value.close();
          buttonExport.value = false;
        });
    };

    const notifications = computed(() => store.getters["grid/getAlarms"]);

    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const { t } = useI18nUtils();
    const toast = useToast();
    const translations = {
      "omnicloud_fr.watch_list.successful_delete": t(
        "omnicloud_fr.watch_list.successful_delete"
      ),
      "omnicloud_fr.watch_list.error_delete": t("omnicloud_fr.watch_list.error_delete"),
    };
    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      tenantOptions,
      typeAlarmsOptions,
    } = useList();

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: context.parent.$i18n.t("messages.delete.title"),
        text: context.parent.$i18n.t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: context.parent.$i18n.t("actions.delete"),
        cancelButtonText: context.parent.$i18n.t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("omnicloud_watch_list/delete", id)
            .then((response) => {
              toast({
                component: ToastificationContent,
                props: {
                  title:
                    response.message ||
                    translations["omnicloud_fr.watch_list.successful_delete"],
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
              self.refetchData();
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title:
                      response.response.data.message ||
                      translations["omnicloud_fr.watch_list.error_delete"],
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    const openAdd = () => {
      isSidebarActive.value = true;
      isAdd.value = true;
    };

    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = item;
      isAdd.value = false;
    };

    return {
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      notifications,
      buttonExport,
      loadingToast,
      action,
      exportExcel,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      tenantOptions,
      update,
      typeAlarmsOptions,
    };
  },
};
</script>
